import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassInput - Executive design system input component
 *
 * A premium text input with glass morphism effects and smooth transitions.
 * Matches the visual style of other Glass components in the design system.
 *
 * @example
 * ```tsx
 * <GlassInput
 *   value={value}
 *   onChange={(value) => setValue(value)}
 *   label="Email Address"
 *   placeholder="Enter your email"
 *   error="Invalid email format"
 * />
 * ```
 */

export interface GlassInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  /** Current input value */
  value: string | number;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Optional label */
  label?: string;
  /** Error message */
  error?: string;
  /** Helper text below the input */
  helperText?: string;
  /** Input size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Expand to full width */
  fullWidth?: boolean;
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side */
  rightIcon?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  sm: 'py-1.5 text-sm',
  md: 'py-2 text-base',
  lg: 'py-2.5 text-lg',
};

const paddingClasses = {
  sm: { default: 'px-3', withLeftIcon: 'pl-9 pr-3', withRightIcon: 'pl-3 pr-9', withBothIcons: 'pl-9 pr-9' },
  md: { default: 'px-4', withLeftIcon: 'pl-10 pr-4', withRightIcon: 'pl-4 pr-10', withBothIcons: 'pl-10 pr-10' },
  lg: { default: 'px-5', withLeftIcon: 'pl-12 pr-5', withRightIcon: 'pl-5 pr-12', withBothIcons: 'pl-12 pr-12' },
};

const iconContainerClasses = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

const leftIconPositionClasses = {
  sm: 'left-2.5',
  md: 'left-3',
  lg: 'left-3.5',
};

const rightIconPositionClasses = {
  sm: 'right-2.5',
  md: 'right-3',
  lg: 'right-3.5',
};

export const GlassInput = forwardRef<HTMLInputElement, GlassInputProps>(
  (
    {
      value,
      onChange,
      label,
      error,
      helperText,
      size = 'md',
      fullWidth = false,
      leftIcon,
      rightIcon,
      className,
      disabled,
      id,
      required,
      ...props
    },
    ref
  ) => {
    const inputId = id || `glass-input-${Math.random().toString(36).substr(2, 9)}`;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    };

    // Determine padding based on icons
    const getPaddingClass = () => {
      if (leftIcon && rightIcon) return paddingClasses[size].withBothIcons;
      if (leftIcon) return paddingClasses[size].withLeftIcon;
      if (rightIcon) return paddingClasses[size].withRightIcon;
      return paddingClasses[size].default;
    };

    const inputClasses = cn(
      // Base styles
      'w-full',
      'rounded-xl',
      'border border-border-primary',
      'bg-white/80 backdrop-blur-sm dark:bg-slate-800/80',
      'text-foreground placeholder-text-tertiary',
      'transition-all duration-200',
      // Focus styles
      'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 focus:border-[var(--accent)]',
      // Hover styles
      'hover:border-border-primary hover:bg-white/90 dark:hover:bg-slate-700/90',
      // Size
      sizeClasses[size],
      getPaddingClass(),
      // Error state
      error && 'border-rose-500/50 focus:ring-rose-500/40 focus:border-rose-500',
      // Disabled state
      disabled && 'opacity-50 cursor-not-allowed bg-bg-tertiary dark:bg-bg-secondary',
      // Custom classes
      className
    );

    return (
      <div className={cn('relative', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-muted-foreground mb-1.5"
          >
            {label}
            {required && <span className="text-rose-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <span
              className={cn(
                'absolute top-1/2 -translate-y-1/2 pointer-events-none',
                'text-muted-foreground/60',
                leftIconPositionClasses[size]
              )}
            >
              <span className={iconContainerClasses[size]}>{leftIcon}</span>
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            required={required}
            className={inputClasses}
            {...props}
          />

          {rightIcon && (
            <span
              className={cn(
                'absolute top-1/2 -translate-y-1/2 pointer-events-none',
                'text-muted-foreground/60',
                rightIconPositionClasses[size]
              )}
            >
              <span className={iconContainerClasses[size]}>{rightIcon}</span>
            </span>
          )}
        </div>

        {error && (
          <p className="mt-1.5 text-sm text-rose-500">{error}</p>
        )}

        {helperText && !error && (
          <p className="mt-1.5 text-sm text-muted-foreground/60">{helperText}</p>
        )}
      </div>
    );
  }
);

GlassInput.displayName = 'GlassInput';
